import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '@/services/api'
import type { User, LoginRequest } from '@/types/auth'

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  refreshToken: string | null
  isLoading: boolean
  error: string | null

  // Actions
  login: (credentials: LoginRequest) => Promise<void>
  loginWithEmail: (email: string, password: string) => Promise<void>
  loginWithPhone: (phone: string, code: string) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<void>
  clearError: () => void
  setUser: (user: User) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
      isLoading: false,
      error: null,

      login: async (credentials) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.login(credentials)
          const { access_token, refresh_token, user } = response.data

          // Set auth header for future requests
          authApi.setAuthHeader(access_token)

          set({
            isAuthenticated: true,
            user,
            token: access_token,
            refreshToken: refresh_token,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            isLoading: false,
            error: error.response?.data?.detail || '登录失败，请重试',
          })
          throw error
        }
      },

      loginWithEmail: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.loginWithEmail(email, password)
          const { access_token, refresh_token, user } = response.data

          // Set auth header for future requests
          authApi.setAuthHeader(access_token)

          set({
            isAuthenticated: true,
            user,
            token: access_token,
            refreshToken: refresh_token,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            isLoading: false,
            error: error.response?.data?.detail || '邮箱登录失败，请重试',
          })
          throw error
        }
      },

      loginWithPhone: async (phone: string, code: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.loginWithPhone(phone, code)
          const { access_token, refresh_token, user } = response.data

          // Set auth header for future requests
          authApi.setAuthHeader(access_token)

          set({
            isAuthenticated: true,
            user,
            token: access_token,
            refreshToken: refresh_token,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            isLoading: false,
            error: error.response?.data?.detail || '手机号登录失败，请重试',
          })
          throw error
        }
      },

      logout: () => {
        const { token } = get()
        if (token) {
          // Call logout API
          authApi.logout(token).catch(console.error)
        }

        // Clear auth header
        authApi.clearAuthHeader()

        set({
          isAuthenticated: false,
          user: null,
          token: null,
          refreshToken: null,
          isLoading: false,
          error: null,
        })
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) {
          get().logout()
          return
        }

        try {
          const response = await authApi.refreshToken(refreshToken)
          const { access_token } = response.data

          authApi.setAuthHeader(access_token)

          set({
            token: access_token,
            error: null,
          })
        } catch (error) {
          // If refresh fails, logout
          get().logout()
          throw error
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user) => set({ user }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
      }),
    }
  )
)

// Initialize auth header on app load
const initializeAuth = () => {
  const state = useAuthStore.getState()
  if (state.token) {
    authApi.setAuthHeader(state.token)
  }
}

// Call this when the app starts
initializeAuth()